import ConfirmAction from '@/components/confirm-action';
import MeterBar from '@/components/console/meter-bar';
import ModeBadge from '@/components/console/mode-badge';
import StatusPill, { type Tone } from '@/components/console/status-pill';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate, formatMoney, formatNumber, humanize } from '@/lib/format';
import PlanCard, { type BillingInterval } from '@/pages/billing/components/plan-card';
import { billing_portal_path, subscription_path } from '@/rails/routes';
import type { MeterRow, Plan, ProviderMode } from '@/types/platform';
import { router } from '@inertiajs/react';
import { useState } from 'react';

interface Subscription {
  status: string;
  billingInterval: BillingInterval;
  currentPeriodEnd: string | null;
  endsAt: string | null;
  demo: boolean;
}

interface Props {
  plan: Plan;
  plans: Plan[];
  meter: MeterRow[];
  subscription: Subscription | null;
  comped: boolean;
  source: 'caprock_client' | 'public';
  billingMode: ProviderMode;
  overage: { emails: number; emailsCents: number; storageCents: number };
  canManage: boolean;
}

const STATUS_TONES: Record<string, Tone> = {
  active: 'success',
  trialing: 'info',
  past_due: 'danger',
  incomplete: 'warning',
  canceled: 'neutral',
};

const INTERVALS: { value: BillingInterval; label: string }[] = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

const Show = ({
  plan,
  plans,
  meter,
  subscription,
  comped,
  source,
  billingMode,
  overage,
  canManage,
}: Props) => {
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(
    subscription?.billingInterval ?? 'month'
  );
  const [busy, setBusy] = useState(false);
  const visitOptions = { onStart: () => setBusy(true), onFinish: () => setBusy(false) };

  const running = subscription !== null && subscription.status !== 'canceled';
  const connected = billingMode !== 'unconfigured';
  const freePlan = plans.find((candidate) => !candidate.paid);
  const hasOverage = overage.emails > 0 || overage.emailsCents > 0 || overage.storageCents > 0;
  // Moving to the free plan cancels the subscription, so both ask the same question first.
  const cancelConfirm = {
    title: 'Cancel your subscription?',
    description: subscription?.demo
      ? 'This is a demo subscription, so it ends now.'
      : `The plan runs to the end of the period you paid for. Then the account moves to ${freePlan?.name ?? 'the free plan'}.`,
    confirmLabel: 'Cancel subscription',
  };

  // The server either sends the owner out to checkout or straight back here.
  const switchTo = (target: Plan) =>
    router.post(
      subscription_path(),
      { plan_key: target.key, interval: billingInterval },
      visitOptions
    );

  return (
    <div className="space-y-8">
      <PageHeader
        title="Plan and usage"
        badges={
          <>
            {source === 'caprock_client' ? <StatusPill>Client account</StatusPill> : null}
            {billingMode === 'demo' ? <ModeBadge mode="demo" /> : null}
          </>
        }
      />

      {billingMode === 'demo' ? (
        <p className="text-muted-foreground text-sm">
          Demo: plan changes take effect at once and nothing is charged.
        </p>
      ) : null}
      {connected ? null : <p className="text-sm text-yellow-800">Billing is not connected yet.</p>}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <h2>{plan.name} plan</h2>
              {running && subscription ? (
                <StatusPill tone={STATUS_TONES[subscription.status] ?? 'neutral'}>
                  {humanize(subscription.status)}
                </StatusPill>
              ) : null}
              {comped ? <StatusPill>Billed directly</StatusPill> : null}
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4 text-sm">
            <BillingSummary subscription={subscription} comped={comped} />

            {canManage && running ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => router.post(billing_portal_path(), {}, visitOptions)}
                >
                  Manage billing
                </Button>
                <ConfirmAction
                  {...cancelConfirm}
                  onConfirm={() => router.delete(subscription_path(), visitOptions)}
                >
                  <Button variant="outline" size="sm" disabled={busy}>
                    Cancel subscription
                  </Button>
                </ConfirmAction>
              </div>
            ) : null}

            {canManage ? null : (
              <p className="text-muted-foreground">An owner or admin changes the plan.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <h2>Usage</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {meter.map((row) => (
              <MeterBar key={row.key} row={row} />
            ))}

            {hasOverage ? (
              <div className="border-t pt-4 text-sm">
                <h3 className="font-medium">Over the plan this month</h3>
                <ul className="mt-1.5 space-y-1">
                  {overage.emails > 0 || overage.emailsCents > 0 ? (
                    <li className="flex items-baseline justify-between gap-3">
                      <span>{formatNumber(overage.emails)} emails over the plan</span>
                      {overage.emailsCents > 0 ? (
                        <span className="tabular-nums">{formatMoney(overage.emailsCents)}</span>
                      ) : null}
                    </li>
                  ) : null}
                  {overage.storageCents > 0 ? (
                    <li className="flex items-baseline justify-between gap-3">
                      <span>Attachments over the plan</span>
                      <span className="tabular-nums">{formatMoney(overage.storageCents)}</span>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <section aria-labelledby="billing-plans" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="billing-plans" className="text-lg font-semibold">
            Plans
          </h2>
          <div role="group" aria-label="Billing period" className="flex gap-1">
            {INTERVALS.map((option) => (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={billingInterval === option.value ? 'secondary' : 'ghost'}
                aria-pressed={billingInterval === option.value}
                onClick={() => setBillingInterval(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {plans.map((candidate) => (
            <PlanCard
              key={candidate.key}
              plan={candidate}
              interval={billingInterval}
              current={candidate.key === plan.key}
              action={
                canManage && !comped ? (
                  candidate.key === plan.key ? (
                    <Button className="w-full" variant="outline" disabled>
                      Current plan
                    </Button>
                  ) : !candidate.paid && running ? (
                    <ConfirmAction {...cancelConfirm} onConfirm={() => switchTo(candidate)}>
                      <Button className="w-full" disabled={!connected || busy}>
                        Switch to {candidate.name}
                      </Button>
                    </ConfirmAction>
                  ) : (
                    <Button
                      className="w-full"
                      disabled={!connected || busy}
                      onClick={() => switchTo(candidate)}
                    >
                      Switch to {candidate.name}
                    </Button>
                  )
                ) : undefined
              }
            />
          ))}
        </div>
      </section>
    </div>
  );
};

interface BillingSummaryProps {
  subscription: Subscription | null;
  comped: boolean;
}

// Who bills this account and when, in a sentence or two.
const BillingSummary = ({ subscription, comped }: BillingSummaryProps) => {
  if (comped) {
    return (
      <p>
        This account is billed by CAP Rock Labs directly. Plan changes go through your contact
        there.
      </p>
    );
  }

  if (subscription && subscription.status !== 'canceled') {
    return (
      <div className="space-y-1">
        <p>Billed {subscription.billingInterval === 'year' ? 'yearly' : 'monthly'}.</p>
        {subscription.endsAt ? (
          <p>Ends on {formatDate(subscription.endsAt)}.</p>
        ) : subscription.currentPeriodEnd ? (
          <p>Current period ends on {formatDate(subscription.currentPeriodEnd)}.</p>
        ) : null}
        {subscription.demo ? (
          <p className="text-muted-foreground">Demo subscription. Nothing is charged.</p>
        ) : null}
      </div>
    );
  }

  if (subscription) {
    return (
      <p>
        Your subscription was canceled
        {subscription.endsAt ? ` on ${formatDate(subscription.endsAt)}` : ''}.
      </p>
    );
  }

  return <p>This plan is free. There is no subscription.</p>;
};

export default Show;
